import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play, ScanSearch, Wrench, Send, Stamp, CircleCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import TechBackground from "@/components/fx/TechBackground";
import ScrambleText from "@/components/fx/ScrambleText";
import CountUp from "@/components/fx/CountUp";
import TiltCard from "@/components/fx/TiltCard";
import { SYNIR_MEDIA, SYNIR_STATS, SYNIR_PILLARS } from "@/data/synir";

const ICONS = { ScanSearch, Wrench, Send, Stamp };
const EASE = [0.22, 1, 0.36, 1];

function PhoneVideo() {
  return (
    <div className="relative mx-auto w-[260px] sm:w-[290px]" data-testid="synir-phone">
      <div aria-hidden className="absolute -inset-10 bg-[#22D3EE]/20 blur-3xl rounded-full glow-breathe" />
      <div className="orbit-ring absolute -inset-8 rounded-[3rem] border border-[#22D3EE]/25" aria-hidden />
      <div className="relative rounded-[2.6rem] p-[3px] border-beam">
        <div className="relative rounded-[2.4rem] bg-black p-2.5 shadow-[0_0_60px_rgba(34,211,238,0.25)]">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-black z-10" />
          <video
            data-testid="synir-loop-video"
            src={SYNIR_MEDIA.loop}
            poster={SYNIR_MEDIA.poster}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full aspect-[9/16] object-cover rounded-[2rem] bg-[#050B18]"
          />
          <div aria-hidden className="absolute inset-2.5 rounded-[2rem] overflow-hidden pointer-events-none">
            <div className="scanline" />
          </div>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.6, duration: 0.6 }}
        className="float-y hidden sm:flex absolute top-16 -left-36 items-center gap-2 px-3 py-2 bg-[#0B1220]/80 backdrop-blur-md border border-[#22D3EE]/30"
      >
        <span className="relative w-1.5 h-1.5 rounded-full bg-[#34D399] text-[#34D399] live-dot" />
        <span className="font-mono-axis text-[10px] uppercase tracking-widest text-[#34D399]">Em operação</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="float-y-slow hidden sm:block absolute bottom-24 -right-40 px-4 py-3 bg-[#0B1220]/80 backdrop-blur-md border border-[#22D3EE]/30"
      >
        <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400">Recusa do TASY</p>
        <p className="font-display text-2xl font-semibold text-[#22D3EE] neon-text">25 → 1</p>
        <p className="text-[10px] text-gray-400">crítica que impede o fechamento</p>
      </motion.div>
    </div>
  );
}

function Pillar({ p, i }) {
  const Icon = ICONS[p.icon];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: 0.1 * i, ease: EASE }}
    >
      <TiltCard
        data-testid={`synir-pillar-${p.id}`}
        className="group h-full bg-white/[0.03] backdrop-blur-md border border-white/10 hover:border-[#22D3EE]/50 p-7 transition-colors duration-300"
      >
        <span aria-hidden className="absolute top-0 left-0 h-[2px] w-12 bg-[#22D3EE] transition-[width] duration-500 group-hover:w-full neon-line" />
        <div className="flex items-center justify-between mb-5">
          <div className="w-11 h-11 border border-[#22D3EE]/40 text-[#22D3EE] flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <span className="font-mono-axis text-xs text-gray-500">{String(i + 1).padStart(2, "0")}</span>
        </div>
        <h4 className="font-display text-xl font-semibold text-white tracking-tight">{p.title}</h4>
        <ul className="mt-4 space-y-3">
          {p.items.map((t) => (
            <li key={t} className="flex gap-2.5 text-sm text-gray-400 leading-relaxed">
              <CircleCheck className="w-4 h-4 mt-0.5 shrink-0 text-[#22D3EE]/70" strokeWidth={1.75} />
              {t}
            </li>
          ))}
        </ul>
      </TiltCard>
    </motion.div>
  );
}

function Stats() {
  return (
    <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
      {SYNIR_STATS.map((s, i) => (
        <div key={s.label} className="bg-[#050B18] p-5" data-testid={`synir-stat-${i}`}>
          <p className="font-display text-4xl lg:text-5xl font-semibold text-[#22D3EE] neon-text">
            <CountUp to={s.value} />
          </p>
          <p className="mt-2 text-xs text-gray-400 leading-snug">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

export default function SynirSection() {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="synir-section" className="relative overflow-hidden bg-[#050B18] text-white py-24 lg:py-32">
      <TechBackground dark density={70} />
      <div className="grain absolute inset-0" aria-hidden />
      <div aria-hidden className="absolute -top-40 left-1/3 w-[700px] h-[700px] rounded-full bg-[#1E3A8A]/30 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#22D3EE]/40 bg-[#22D3EE]/5 text-[#22D3EE] font-mono-axis text-xs uppercase tracking-widest">
              <span className="relative w-1.5 h-1.5 rounded-full bg-[#22D3EE] text-[#22D3EE] live-dot" />
              Lançamento · Integrado ao TASY
            </div>
            <h2 className="mt-8 font-display font-semibold tracking-tighter leading-[0.95]">
              <ScrambleText
                text="SYNIR"
                duration={1200}
                data-testid="synir-title"
                className="block text-6xl sm:text-7xl lg:text-8xl text-[#22D3EE] neon-text"
              />
              <span className="mt-4 block text-3xl sm:text-4xl lg:text-5xl text-white">
                Conta parada é receita parada.
              </span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-gray-400 leading-relaxed max-w-2xl">
              O SYNIR é um <span className="text-white font-medium">motor de auditoria e faturamento para o Pronto Atendimento</span>,
              integrado ao TASY. Ele lê a guia e os anexos da conta, confere evoluções, laudos, autorizações e materiais,
              abre as pendências para o setor responsável e, quando a conta está pronta, ele mesmo fecha e protocola no
              mês certo. O próximo passo é fazer também a entrega ao convênio.
            </p>
            <Stats />
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="synir-watch-video-btn"
                className="group inline-flex items-center gap-3 px-6 py-4 bg-[#22D3EE] text-[#050B18] text-sm font-semibold hover:bg-white transition-colors shadow-[0_0_30px_rgba(34,211,238,0.45)]"
              >
                <Play className="w-4 h-4 fill-current transition-transform group-hover:scale-125" />
                Assistir vídeo completo
              </button>
              <Link
                to="/contato"
                data-testid="synir-demo-btn"
                className="group inline-flex items-center gap-2 px-6 py-4 border border-white/20 text-sm font-medium hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors"
              >
                Quero o SYNIR no meu hospital
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
              </Link>
            </div>
          </motion.div>
          <div className="lg:col-span-5">
            <PhoneVideo />
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-6">
          {SYNIR_PILLARS.map((p, i) => (
            <Pillar key={p.id} p={p} i={i} />
          ))}
        </div>

        <motion.blockquote
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 max-w-4xl border-l-2 border-[#22D3EE] pl-8"
        >
          <p className="font-display text-2xl lg:text-3xl tracking-tight leading-snug text-white">
            “O faturamento deixa de caçar erro e de fechar conta por conta,{" "}
            <span className="text-[#22D3EE]">e passa a acompanhar o fluxo.</span>”
          </p>
          <p className="mt-4 text-sm text-gray-400 max-w-2xl">
            A enfermagem, o laboratório e a recepção recebem só o que é delas, com a explicação do que falta.
          </p>
        </motion.blockquote>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="synir-video-modal" className="max-w-md p-0 bg-black border-[#22D3EE]/30 overflow-hidden">
          <DialogTitle className="sr-only">Vídeo SYNIR</DialogTitle>
          {open && (
            <video
              data-testid="synir-full-video"
              src={SYNIR_MEDIA.full}
              controls
              autoPlay
              playsInline
              className="w-auto mx-auto max-h-[85vh] bg-black"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
